import { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  FormModal,
  Input,
  Page,
  Table,
  Text,
} from "@marekl/adminer/components";
import { useConfirm, useT } from "@marekl/adminer/hooks";
import { apiErrorMessage, notifyError } from "@marekl/adminer/api";
import { reorderRows } from "./reorder";
import { DisciplineFormModal } from "../../../components/DisciplineFormModal";
import { RuleChecks } from "../../../components/RuleChecks";
import {
  familyName,
  useDeleteDiscipline,
  useDuplicateDiscipline,
  useReorderDisciplines,
  useSetFamily,
  type Discipline,
  type DisciplineFamily,
  type RuleSetWorkspace,
} from "../../../lib/queries/rules";
import {
  describeIssue,
  disciplineIssues,
  summarizeDisciplineIssues,
} from "../../../lib/rulesChecks";
import tableStyles from "./componentTables.module.css";
import { RowLink } from "../../../components/RowLink";
import type { ID } from "../../../lib/id";

type ModalState =
  | { kind: "closed" }
  | { kind: "create" }
  | { kind: "renameFamily"; code: string; name: string };

/**
 * The performance tiers a discipline runs, as "A" or "A+B".
 *
 * Single-tier disciplines are named too, not left blank. A blank cell
 * cannot say whether the discipline runs one tier or has no class table
 * worth speaking of, and the reader should not have to open the page to
 * find out which.
 */
function classTiers(workspace: RuleSetWorkspace, d: Discipline): string | null {
  const table = workspace.performance_class_tables.find(
    (t) => t.id === d.performance_class_table_id,
  );
  const classes = table?.classes ?? [];
  if (classes.length === 0) return null;
  return classes.map((c) => c.code).join("+");
}

/**
 * Groups disciplines by family, in the family order the rule set
 * declares.
 *
 * The order comes from the family ROWS, not from the disciplines: it is
 * the first key of the canonical category order, so the groups here read
 * in the same sequence as „Soutěžní kategorie" and the start list. A
 * family with no row of its own (Discipline.family is a free string)
 * keeps its first-seen position at the end, which is where a typo
 * belongs — visible.
 */
function byFamily(
  disciplines: Discipline[],
  families: DisciplineFamily[],
): { family: string; rows: Discipline[] }[] {
  const groups: { family: string; rows: Discipline[] }[] = [];
  for (const d of disciplines) {
    const group = groups.find((g) => g.family === d.family);
    if (group) group.rows.push(d);
    else groups.push({ family: d.family, rows: [d] });
  }
  const rank = new Map(families.map((f) => [f.code, f.sort_order]));
  return groups.sort((a, b) => {
    const ra = rank.get(a.family);
    const rb = rank.get(b.family);
    if (ra === undefined && rb === undefined) return 0;
    if (ra === undefined) return 1;
    if (rb === undefined) return -1;
    return ra - rb;
  });
}

export function DisciplinesTab({
  ruleSetId,
  readOnly,
  structureLocked,
  workspace,
}: {
  ruleSetId: ID;
  readOnly: boolean;
  /** Set is in service: disciplines may not be deleted and axis tables not swapped. */
  structureLocked: boolean;
  workspace: RuleSetWorkspace;
}) {
  const {
    disciplines,
    age_tables,
    formation_tables,
    performance_class_tables,
    recordings,
  } = workspace;
  // Which official tracks exist. The checks resolve each combination's
  // recording against this, because a discipline's default is not the
  // whole answer — TD Twirl's tým combinations override it.
  const recordingIds = new Set(recordings.map((r) => r.id));
  const confirm = useConfirm();
  const t = useT();
  const [modal, setModal] = useState<ModalState>({ kind: "closed" });
  const remove = useDeleteDiscipline();
  const duplicate = useDuplicateDiscipline();
  const setFamily = useSetFamily(ruleSetId);
  const reorderDisciplines = useReorderDisciplines(ruleSetId);

  const groups = byFamily(disciplines, workspace.families);
  const familyBusy = setFamily.isPending;

  // Both moves follow the pattern the component cards use: renumber
  // locally, then write only the rows whose position actually changed.
  //
  // They pass their own `onError` because the shared rules mutations
  // suppress the global error toast — without one a refused reorder is
  // silent, which is the bug the 0.24.1 sweep flushed out elsewhere.
  //
  // A family swap is TWO writes, and they go one after the other rather
  // than both at once. Families have no whole-order endpoint the way
  // disciplines do — a family row is upserted by code and may not exist
  // yet — so the two positions cannot be written atomically; firing them
  // in parallel only widens the window in which one lands and the other
  // is refused, leaving two families sharing a position. Sequential, the
  // first failure stops the second, so the stored order is either the
  // old one or the new one.
  const moveFamily = async (index: number, direction: -1 | 1) => {
    const ordered = groups.map(({ family }, i) => ({
      id: String(i),
      code: family,
      sort_order: i,
    }));
    try {
      for (const { row, sort_order } of reorderRows(ordered, index, direction)) {
        await setFamily.mutateAsync({
          code: row.code,
          // The name is required by the endpoint and the display name is
          // what the reader sees, so send the resolved one rather than
          // the code — otherwise a reorder would rename an unnamed
          // family to its own code.
          name: familyName(workspace.families, row.code),
          sort_order,
        });
      }
    } catch (err) {
      notifyError(err);
    }
  };

  // Within one family only. Moving a discipline across families is a
  // rename of its `family` field, which the edit dialog already does —
  // and doing it from an arrow would be a structural change disguised as
  // a nudge.
  //
  // The request carries the WHOLE set's order, not just this family's,
  // because the server treats a partial list as a stale client and
  // refuses it. Swapping two neighbours inside one group and then
  // flattening the groups gives exactly that: every discipline, once.
  const moveDiscipline = (family: string, index: number, direction: -1 | 1) => {
    const next = groups.map((g) => {
      if (g.family !== family) return g.rows;
      const rows = [...g.rows];
      const target = index + direction;
      if (target < 0 || target >= rows.length) return rows;
      [rows[index], rows[target]] = [rows[target], rows[index]];
      return rows;
    });
    reorderDisciplines.mutate(
      { ids: next.flat().map((d) => d.id) },
      { onError: (err) => notifyError(err) },
    );
  };

  const close = () => {
    setModal({ kind: "closed" });
    duplicate.reset();
    setFamily.reset();
  };

  // Opening a dialog clears whatever the last mutation said, because
  // `setFamily` is shared: the reorder arrows fire it too, and its error
  // is what FormModal renders. Without this, a refused arrow click left
  // its message sitting inside „Přejmenovat skupinu" the next time that
  // dialog opened — an unrelated refusal, attached to a form nobody had
  // submitted yet.
  const openModal = (next: ModalState) => {
    duplicate.reset();
    setFamily.reset();
    setModal(next);
  };

  const missingTables =
    age_tables.length === 0 ||
    formation_tables.length === 0 ||
    performance_class_tables.length === 0;

  return (
    <>
      <Page.SectionHeader
        title="Disciplíny"
        meta={
          readOnly ? undefined : (
            <Button
              variant="primary"
              disabled={missingTables}
              onClick={() => openModal({ kind: "create" })}
            >
              Nová disciplína
            </Button>
          )
        }
      />
      {missingTables && !readOnly && (
        <Alert variant="warn">
          Než založíte disciplínu, vytvořte alespoň jednu tabulku věkových kategorií,
          jednu tabulku formací a jednu tabulku výkonnostních tříd; z nich vzniknou
          její kombinace.
        </Alert>
      )}

      {/* The same checks the discipline's own page runs, rolled up:
          without them the list looked settled while every combination
          was still missing a scoring sheet. */}
      {duplicate.error && <Alert variant="err">{apiErrorMessage(duplicate.error, t)}</Alert>}

      <RuleChecks
        warnings={summarizeDisciplineIssues(disciplines, recordingIds)}
      />

      {disciplines.length === 0 ? (
        <EmptyState
          title="Žádné disciplíny"
          description="Disciplína spojí tabulku věkových kategorií, tabulku formací a tabulku výkonnostních tříd a vytvoří z nich kombinace."
        />
      ) : (
        groups.map(({ family, rows }, familyIndex) => (
          <Card key={family}>
            <Card.Header
              title={familyName(workspace.families, family)}
              subtitle={family}
              subtitlePlacement="right"
              actions={
                readOnly ? undefined : (
                  <>
                    {/* The order of the families is the first key of the
                        canonical category order, so this is what an
                        organizer changes when the programme should read
                        differently. Not a structural edit: it cannot make
                        a combination disappear, so it stays available on
                        a set that is already in service. */}
                    <Button
                      weight="ghost"
                      aria-label="Posunout skupinu nahoru"
                      disabled={familyIndex === 0 || familyBusy}
                      onClick={() => moveFamily(familyIndex, -1)}
                    >
                      <ChevronUp aria-hidden="true" />
                    </Button>
                    <Button
                      weight="ghost"
                      aria-label="Posunout skupinu dolů"
                      disabled={familyIndex === groups.length - 1 || familyBusy}
                      onClick={() => moveFamily(familyIndex, 1)}
                    >
                      <ChevronDown aria-hidden="true" />
                    </Button>
                    <Button
                      weight="ghost"
                      onClick={() =>
                        openModal({
                          kind: "renameFamily",
                          code: family,
                          name: familyName(workspace.families, family),
                        })
                      }
                    >
                      Přejmenovat
                    </Button>
                  </>
                )
              }
            />
            <Card.Content padding="none">
              <Table<Discipline>
                rows={rows}
                rowKey={(d) => d.id}
                dense
                columns={[
                  {
                    key: "name",
                    header: "Název",
                    tdClass: tableStyles.disciplineName,
                    render: (d) => {
                      const tiers = classTiers(workspace, d);
                      return (
                        <>
                          <RowLink to={`/admin/pravidla/${ruleSetId}/disciplina/${d.id}`}>
                            {d.name}
                          </RowLink>
                          {tiers && (
                            <>
                              {" "}
                              <Badge variant="muted">{tiers}</Badge>
                            </>
                          )}
                        </>
                      );
                    },
                  },
                  {
                    key: "apparatus",
                    header: "Náčiní",
                    tdClass: tableStyles.disciplineApparatus,
                    render: (d) => d.apparatus || <Text variant="muted">—</Text>,
                  },
                  {
                    // The formation ladder is the axis that actually
                    // varies between disciplines, and it is what explains
                    // the combination count beside it. The other two axes
                    // are the same on sixteen and seventeen of twenty-one
                    // rows, so they stay on the discipline's own page —
                    // as do the default time and floor, which a quarter
                    // of the combinations override anyway.
                    key: "formations",
                    header: "Formace",
                    tdClass: tableStyles.disciplineFormations,
                    render: (d) =>
                      workspace.formation_tables.find((t) => t.id === d.formation_table_id)?.name ?? (
                        <Text variant="muted">—</Text>
                      ),
                  },
                  {
                    key: "combos",
                    header: "Kombinací",
                    numeric: true,
                    tdClass: tableStyles.disciplineCount,
                    render: (d) => {
                      const all = d.categories ?? [];
                      const enabled = all.filter((c) => c.enabled).length;
                      return (
                        <Text variant="mono">
                          {enabled}/{all.length}
                        </Text>
                      );
                    },
                  },
                  {
                    key: "issues",
                    header: "Kontrola",
                    tdClass: tableStyles.disciplineIssues,
                    render: (d) => {
                      const issues = disciplineIssues(d, recordingIds);
                      if (issues.length === 0) return <Text variant="muted">—</Text>;
                      // The count alone would not say what is wrong, and
                      // the sentences are too long for a cell; they live
                      // in the tooltip and in full on the discipline page.
                      return (
                        <span title={issues.map(describeIssue).join("\n")}>
                          <Badge variant="warn">{issues.length} upozornění</Badge>
                        </span>
                      );
                    },
                  },
                  {
                    key: "actions",
                    header: "",
                    render: (d) => (
                      <Table.Actions>
                        {/* Order within the family. Across families is a
                            different act — it changes `family`, which the
                            edit dialog owns — so these two only ever move a
                            row inside the card it is in. */}
                        {!readOnly && (
                          <>
                            <Table.Action
                              appearance="button"
                              iconOnly
                              aria-label="Posunout nahoru"
                              disabled={rows.indexOf(d) === 0 || reorderDisciplines.isPending}
                              onClick={() => moveDiscipline(family, rows.indexOf(d), -1)}
                            >
                              <ChevronUp aria-hidden="true" />
                            </Table.Action>
                            <Table.Action
                              appearance="button"
                              iconOnly
                              aria-label="Posunout dolů"
                              disabled={
                                rows.indexOf(d) === rows.length - 1 || reorderDisciplines.isPending
                              }
                              onClick={() => moveDiscipline(family, rows.indexOf(d), 1)}
                            >
                              <ChevronDown aria-hidden="true" />
                            </Table.Action>
                          </>
                        )}
                        {/* Editing a discipline means its whole page —
                            defaults and matrix together — so there is no
                            separate detail link. */}
                        <Table.Action appearance="button" to={`/admin/pravidla/${ruleSetId}/disciplina/${d.id}`}>
                          {readOnly ? "Zobrazit" : "Upravit"}
                        </Table.Action>
                        {!readOnly && (
                          <Table.Action
                            appearance="button"
                            // The row being copied says „working" (loading);
                            // the others wait, since one mutation tracks one row.
                            loading={duplicate.isPending && duplicate.variables?.id === d.id}
                            disabled={duplicate.isPending && duplicate.variables?.id !== d.id}
                            onClick={() => duplicate.mutate({ id: d.id })}
                          >
                            Duplikovat
                          </Table.Action>
                        )}
                        {/* Deleting takes the discipline's whole combination
                            matrix with it, so a set in service hides it. */}
                        {!readOnly && !structureLocked && (
                          <Table.Action
                            appearance="button"
                            variant="danger"
                            onClick={() =>
                              confirm({
                                title: "Smazat disciplínu",
                                description: (
                                  <>
                                    Smaže se disciplína <strong>{d.name}</strong> i jejích kombinací
                                    ({(d.categories ?? []).length}).
                                  </>
                                ),
                                confirmLabel: "Smazat",
                                variant: "danger",
                                onConfirm: () => remove.mutateAsync(d.id),
                              })
                            }
                          >
                            Smazat
                          </Table.Action>
                        )}
                      </Table.Actions>
                    ),
                  },
                ]}
              />
            </Card.Content>
          </Card>
        ))
      )}

      {modal.kind === "create" && (
        <DisciplineFormModal open workspace={workspace} onClose={close} />
      )}

      <FormModal
        open={modal.kind === "renameFamily"}
        title="Přejmenovat skupinu disciplín"
        submitLabel="Uložit"
        busy={setFamily.isPending}
        canSubmit={modal.kind === "renameFamily" && !!modal.name.trim()}
        error={setFamily.error}
        onSubmit={() => {
          if (modal.kind !== "renameFamily") return;
          setFamily.mutate(
            { code: modal.code, name: modal.name.trim() },
            { onSuccess: close },
          );
        }}
        onClose={close}
      >
        {modal.kind === "renameFamily" && (
          <>
            <Text variant="muted">
              Plný název skupiny „{modal.code}“; zobrazí se místo kódu v seznamu
              disciplín i v průvodci přihláškou.
            </Text>
            <Input
              label="Název"
              value={modal.name}
              onChange={(e) => setModal({ ...modal, name: e.target.value })}
            />
          </>
        )}
      </FormModal>

    </>
  );
}
